import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  Briefcase,
  Clock,
  Coins,
  Droplets,
  Sprout,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { Area, AreaChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatUnits } from 'viem';
import type { Address } from 'viem';
import {
  useAccount,
  useBalance,
  useReadContracts,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';
import { MARKET_PAIRS } from '../data/mockMarket';
import { ERC20_ABI, FARM_ABI, PAIR_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment } from '../web3/contracts';
import { logTransaction, type TxType } from '../web3/logTransaction';
import { readPortfolioHistory, recordPortfolioValue } from '../web3/portfolioHistory';
import { useMyActivity } from '../web3/useMyActivity';
import { useChainlinkPrice } from '../web3/useChainlinkPrice';
import { useWallet } from '../web3/useWallet';
import TokenIcon from './TokenIcon';

const COLORS = ['#22d3a8', '#38bdf8', '#f0b90b', '#c084fc', '#f5484f', '#94a3b8', '#fb923c', '#a3e635'];

const TX_LABEL: Partial<Record<TxType, string>> = {
  swap: '스왑',
  addLiquidity: '유동성 추가',
  removeLiquidity: '유동성 제거',
  stake: '스테이킹',
  unstake: '언스테이킹',
  harvest: '보상 수확',
  rwaBuy: 'SALT 매수',
  rwaSell: 'SALT 매도',
  rwaRedeem: 'SALT 상환',
  launchpadBuy: '런치패드 구매',
  lendDeposit: '담보 예치',
  lendWithdraw: '담보 출금',
  borrow: '대출',
  repay: '상환',
  predictionBet: '예측 베팅',
  predictionClaim: '예측 수령',
  bridgeLock: '브릿지 락',
  bridgeUnlock: '브릿지 해제',
};

const toNum = (v: bigint) => Number(formatUnits(v, 18));

export default function PortfolioView() {
  const { address, isConnected } = useAccount();

  if (!isConnected || !address) {
    return <ConnectPrompt />;
  }
  return <PortfolioContent address={address} />;
}

function ConnectPrompt() {
  const { connectWallet, isPending } = useWallet();
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
      <div className="w-14 h-14 rounded-2xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
        <Briefcase size={24} />
      </div>
      <h2 className="text-xl font-semibold text-foam-100">Portfolio</h2>
      <p className="text-sm text-foam-500 max-w-sm">
        지갑을 연결하면 보유 토큰, LP 포지션, Farm 스테이킹, SALT까지 모든 자산을 한 화면에서 볼 수
        있습니다.
      </p>
      <button
        onClick={connectWallet}
        disabled={isPending}
        className="mt-1 px-4 py-2 rounded-lg text-sm font-semibold bg-tide-500 text-abyss-950 hover:bg-tide-400 transition-colors disabled:opacity-60"
      >
        {isPending ? 'Connecting...' : 'Connect Wallet'}
      </button>
    </div>
  );
}

function PortfolioContent({ address }: { address: Address }) {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const bnbChainlinkPrice = useChainlinkPrice('BNB');
  const btcbChainlinkPrice = useChainlinkPrice('BTCB');
  const ethChainlinkPrice = useChainlinkPrice('ETH');

  const { data: bnbBalance } = useBalance({ address, chainId: readChainId });

  // pair list: every AMM pool plus the Salt RWA pool
  const pairDefs = useMemo(() => {
    if (!deployment) return [];
    const defs = MARKET_PAIRS.map((p) => ({ id: p.id, address: deployment.pairs[p.id] })).filter((d) =>
      Boolean(d.address),
    );
    if (deployment.saltRwa) defs.push({ id: 'SALT-USDT', address: deployment.saltRwa.pair });
    return defs;
  }, [deployment]);

  const pairContracts = useMemo(
    () =>
      pairDefs.flatMap((def) => [
        { address: def.address, abi: PAIR_ABI, functionName: 'getReserves', chainId: readChainId } as const,
        { address: def.address, abi: PAIR_ABI, functionName: 'token0', chainId: readChainId } as const,
        { address: def.address, abi: PAIR_ABI, functionName: 'totalSupply', chainId: readChainId } as const,
        {
          address: def.address,
          abi: ERC20_ABI,
          functionName: 'balanceOf',
          args: [address],
          chainId: readChainId,
        } as const,
      ]),
    [pairDefs, address, readChainId],
  );
  const { data: pairData } = useReadContracts({
    contracts: pairContracts,
    query: { enabled: pairContracts.length > 0, refetchInterval: 15000 },
  });

  // wallet ERC-20 balances (BNB itself is native, handled by useBalance above)
  const tokenSymbols = useMemo(() => {
    if (!deployment) return [];
    const symbols = Object.keys(deployment.tokens).filter((s) => s !== 'BNB');
    if (deployment.saltRwa) symbols.push('SALT');
    return symbols;
  }, [deployment]);

  const tokenContracts = useMemo(
    () =>
      tokenSymbols.map(
        (symbol) =>
          ({
            address: symbol === 'SALT' ? deployment!.saltRwa!.token : deployment!.tokens[symbol],
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [address],
            chainId: readChainId,
          }) as const,
      ),
    [tokenSymbols, deployment, address, readChainId],
  );
  const { data: tokenData } = useReadContracts({
    contracts: tokenContracts,
    query: { enabled: tokenContracts.length > 0, refetchInterval: 15000 },
  });

  const farmDefs = useMemo(
    () => Object.entries(deployment?.farmPools ?? {}).map(([poolId, pid]) => ({ poolId, pid })),
    [deployment],
  );
  const farmContracts = useMemo(
    () =>
      farmDefs.flatMap((def) => [
        {
          address: deployment!.farm!,
          abi: FARM_ABI,
          functionName: 'userInfo',
          args: [BigInt(def.pid), address],
          chainId: readChainId,
        } as const,
        {
          address: deployment!.farm!,
          abi: FARM_ABI,
          functionName: 'pendingReward',
          args: [BigInt(def.pid), address],
          chainId: readChainId,
        } as const,
      ]),
    [farmDefs, deployment, address, readChainId],
  );
  const { data: farmData } = useReadContracts({
    contracts: farmContracts,
    query: { enabled: farmContracts.length > 0 && Boolean(deployment?.farm), refetchInterval: 15000 },
  });

  const model = useMemo(() => {
    if (!deployment || !pairData) return undefined;

    // address → symbol, for pricing pair reserves
    const addrToSymbol = new Map<string, string>();
    for (const [symbol, addr] of Object.entries(deployment.tokens)) {
      addrToSymbol.set(addr.toLowerCase(), symbol);
    }
    if (deployment.saltRwa) addrToSymbol.set(deployment.saltRwa.token.toLowerCase(), 'SALT');

    interface PairInfo {
      id: string;
      reserve0: bigint;
      reserve1: bigint;
      symbol0: string;
      symbol1: string;
      totalSupply: bigint;
      lpBalance: bigint;
    }
    const pairs: PairInfo[] = [];
    pairDefs.forEach((def, i) => {
      const reserves = pairData[i * 4];
      const token0 = pairData[i * 4 + 1];
      const totalSupply = pairData[i * 4 + 2];
      const lpBalance = pairData[i * 4 + 3];
      if (
        reserves.status !== 'success' ||
        token0.status !== 'success' ||
        totalSupply.status !== 'success' ||
        lpBalance.status !== 'success'
      )
        return;
      const [r0, r1] = reserves.result as readonly [bigint, bigint, number];
      const t0 = (token0.result as string).toLowerCase();
      const [symA, symB] = def.id.split('-');
      const addrA = (symA === 'SALT' ? deployment.saltRwa!.token : deployment.tokens[symA]).toLowerCase();
      const symbol0 = t0 === addrA ? symA : symB;
      const symbol1 = t0 === addrA ? symB : symA;
      pairs.push({
        id: def.id,
        reserve0: r0,
        reserve1: r1,
        symbol0,
        symbol1,
        totalSupply: totalSupply.result as bigint,
        lpBalance: lpBalance.result as bigint,
      });
    });

    // spot prices in USDT from X-USDT pool reserve ratios (no swap slippage)
    const price = new Map<string, number>([['USDT', 1]]);
    for (const p of pairs) {
      if (p.symbol0 === 'USDT' || p.symbol1 === 'USDT') {
        const other = p.symbol0 === 'USDT' ? p.symbol1 : p.symbol0;
        const rUsdt = p.symbol0 === 'USDT' ? p.reserve0 : p.reserve1;
        const rOther = p.symbol0 === 'USDT' ? p.reserve1 : p.reserve0;
        if (rOther > 0n) price.set(other, toNum(rUsdt) / toNum(rOther));
      }
    }

    // Prefer manipulation-resistant Chainlink prices for supported assets.
    // Missing, zero, or invalid oracle answers leave the AMM pool price as fallback.
    for (const [symbol, oraclePrice] of [
      ['BNB', bnbChainlinkPrice],
      ['BTCB', btcbChainlinkPrice],
      ['ETH', ethChainlinkPrice],
    ] as const) {
      if (oraclePrice !== undefined && Number.isFinite(oraclePrice) && oraclePrice > 0) {
        price.set(symbol, oraclePrice);
      }
    }

    const walletRows: { symbol: string; balance: number; value: number }[] = [];
    if (bnbBalance) {
      const bal = toNum(bnbBalance.value);
      if (bal > 0) walletRows.push({ symbol: 'BNB', balance: bal, value: bal * (price.get('BNB') ?? 0) });
    }
    tokenSymbols.forEach((symbol, i) => {
      const res = tokenData?.[i];
      if (!res || res.status !== 'success') return;
      const bal = toNum(res.result as bigint);
      if (bal > 0) walletRows.push({ symbol, balance: bal, value: bal * (price.get(symbol) ?? 0) });
    });

    const lpRows: { id: string; balance: number; sharePct: number; value: number }[] = [];
    for (const p of pairs) {
      if (p.lpBalance === 0n || p.totalSupply === 0n) continue;
      const share = toNum(p.lpBalance) / toNum(p.totalSupply);
      const value =
        share *
        (toNum(p.reserve0) * (price.get(p.symbol0) ?? 0) + toNum(p.reserve1) * (price.get(p.symbol1) ?? 0));
      lpRows.push({ id: p.id, balance: toNum(p.lpBalance), sharePct: share * 100, value });
    }

    const farmRows: { poolId: string; pid: number; staked: number; pending: number; value: number }[] = [];
    farmDefs.forEach((def, i) => {
      const userInfo = farmData?.[i * 2];
      const pending = farmData?.[i * 2 + 1];
      if (!userInfo || userInfo.status !== 'success') return;
      const staked = (userInfo.result as readonly [bigint, bigint])[0];
      const pendingPlg = pending?.status === 'success' ? (pending.result as bigint) : 0n;
      if (staked === 0n && pendingPlg === 0n) return;
      const pair = pairs.find((p) => p.id === def.poolId);
      let stakedValue = 0;
      if (pair && pair.totalSupply > 0n) {
        const share = toNum(staked) / toNum(pair.totalSupply);
        stakedValue =
          share *
          (toNum(pair.reserve0) * (price.get(pair.symbol0) ?? 0) +
            toNum(pair.reserve1) * (price.get(pair.symbol1) ?? 0));
      }
      const pendingValue = toNum(pendingPlg) * (price.get('PLG') ?? 0);
      farmRows.push({
        poolId: def.poolId,
        pid: def.pid,
        staked: toNum(staked),
        pending: toNum(pendingPlg),
        value: stakedValue + pendingValue,
      });
    });

    const walletTotal = walletRows.reduce((a, r) => a + r.value, 0);
    const lpTotal = lpRows.reduce((a, r) => a + r.value, 0);
    const farmTotal = farmRows.reduce((a, r) => a + r.value, 0);
    const pendingPlgTotal = farmRows.reduce((a, r) => a + r.pending, 0);
    const plgPrice = price.get('PLG') ?? 0;

    return {
      walletRows,
      lpRows,
      farmRows,
      walletTotal,
      lpTotal,
      farmTotal,
      pendingPlgTotal,
      plgPrice,
      total: walletTotal + lpTotal + farmTotal,
    };
  }, [
    deployment,
    pairData,
    pairDefs,
    tokenData,
    tokenSymbols,
    farmData,
    farmDefs,
    bnbBalance,
    bnbChainlinkPrice,
    btcbChainlinkPrice,
    ethChainlinkPrice,
  ]);

  const total = model?.total;
  useEffect(() => {
    if (total !== undefined && total > 0) recordPortfolioValue(address, total);
  }, [address, total]);

  const activity = useMyActivity(address);

  if (!model) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-foam-500">자산을 조회하는 중...</div>
    );
  }

  const allocation = [
    ...model.walletRows.map((r) => ({ name: r.symbol, value: r.value })),
    ...(model.lpTotal > 0 ? [{ name: 'LP 포지션', value: model.lpTotal }] : []),
    ...(model.farmTotal > 0 ? [{ name: 'Farm 스테이킹', value: model.farmTotal }] : []),
  ].filter((a) => a.value > 0);

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
          <Briefcase size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foam-100">Portfolio</h2>
          <p className="text-xs text-foam-500 mt-0.5">지갑·LP·Farm·RWA 자산 통합 현황 (AMM 풀 가격 기준)</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ValueChart account={address} total={model.total} />
        </div>
        <div className="space-y-6">
          <RewardsCard
            pendingPlg={model.pendingPlgTotal}
            plgPrice={model.plgPrice}
            farmRows={model.farmRows}
            farmAddress={deployment?.farm}
          />
          <div className="grid grid-cols-3 gap-3">
            <MiniStat icon={Wallet} label="지갑" value={model.walletTotal} />
            <MiniStat icon={Droplets} label="LP" value={model.lpTotal} />
            <MiniStat icon={Sprout} label="Farm" value={model.farmTotal} />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="p-5 rounded-xl border border-abyss-700 bg-abyss-850">
          <h3 className="text-sm font-semibold text-foam-100 mb-3">자산 배분</h3>
          {allocation.length === 0 ? (
            <div className="text-xs text-foam-500 py-8 text-center">보유 자산이 없습니다.</div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-36 h-36 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={allocation} dataKey="value" nameKey="name" innerRadius={38} outerRadius={64} strokeWidth={0}>
                      {allocation.map((entry, i) => (
                        <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#0d1420', border: '1px solid #182437', borderRadius: 8, fontSize: 12 }}
                      formatter={(value) => [`$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}`]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2 text-xs min-w-0">
                {allocation.map((entry, i) => {
                  const pct = model.total > 0 ? (entry.value / model.total) * 100 : 0;
                  return (
                    <div key={entry.name}>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-foam-300 truncate">{entry.name}</span>
                        <span className="text-foam-500 tabular-nums ml-auto">{pct.toFixed(1)}%</span>
                      </div>
                      <div className="h-1 rounded-full bg-abyss-900 overflow-hidden mt-1 ml-4">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <ActivityFeed items={activity} />
      </div>

      <Panel title="보유 토큰">
        {model.walletRows.length === 0 ? (
          <Empty text="보유한 토큰이 없습니다." />
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-foam-500 text-left">
                <th className="font-medium pb-2">토큰</th>
                <th className="font-medium pb-2 text-right">수량</th>
                <th className="font-medium pb-2 text-right">평가액 (USD)</th>
              </tr>
            </thead>
            <tbody>
              {model.walletRows.map((r) => (
                <tr key={r.symbol} className="border-t border-abyss-700">
                  <td className="py-2">
                    <div className="flex items-center gap-2 text-foam-100 font-medium">
                      <TokenIcon symbol={r.symbol} size={18} />
                      {r.symbol}
                    </div>
                  </td>
                  <td className="py-2 text-right tabular-nums text-foam-300">
                    {r.balance.toLocaleString('en-US', { maximumFractionDigits: 6 })}
                  </td>
                  <td className="py-2 text-right tabular-nums text-foam-100">
                    ${r.value.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="LP 포지션">
          {model.lpRows.length === 0 ? (
            <Empty text="유동성 포지션이 없습니다." />
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-foam-500 text-left">
                  <th className="font-medium pb-2">풀</th>
                  <th className="font-medium pb-2 text-right">지분</th>
                  <th className="font-medium pb-2 text-right">평가액</th>
                </tr>
              </thead>
              <tbody>
                {model.lpRows.map((r) => (
                  <tr key={r.id} className="border-t border-abyss-700">
                    <td className="py-2 text-foam-100 font-medium">{r.id}</td>
                    <td className="py-2 text-right tabular-nums text-foam-300">{r.sharePct.toFixed(4)}%</td>
                    <td className="py-2 text-right tabular-nums text-foam-100">
                      ${r.value.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Farm 스테이킹">
          {model.farmRows.length === 0 ? (
            <Empty text="스테이킹 중인 자산이 없습니다." />
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-foam-500 text-left">
                  <th className="font-medium pb-2">풀</th>
                  <th className="font-medium pb-2 text-right">스테이킹 LP</th>
                  <th className="font-medium pb-2 text-right">미수령 PLG</th>
                  <th className="font-medium pb-2 text-right">동작</th>
                </tr>
              </thead>
              <tbody>
                {model.farmRows.map((r) => (
                  <FarmHarvestRow key={r.poolId} row={r} farmAddress={deployment?.farm} account={address} />
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-850 text-[11px] text-foam-500 leading-relaxed">
        평가액은 이 DEX의 AMM 풀 스팟 가격 기준입니다. 테스트넷 풀은 유동성이 작아 실제 시장 가격과 다를
        수 있습니다 — 실세계 가격은 Swap 화면의 실시간 시세(Chainlink) 배지를 참고하세요.
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }: { icon: typeof Wallet; label: string; value: number }) {
  return (
    <div className="p-3 rounded-lg bg-abyss-900/60 border border-abyss-700">
      <div className="flex items-center gap-1.5 text-[11px] text-foam-500">
        <Icon size={12} />
        {label}
      </div>
      <div className="text-sm font-semibold text-foam-100 mt-1 tabular-nums">
        ${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}

// portfolio value over time: real samples accumulate in localStorage every ~20s
// while the tab is open; a short deterministic lead-in keeps the chart from
// looking empty on the first visit.
function ValueChart({ account, total }: { account: string; total: number }) {
  const [history, setHistory] = useState(() => readPortfolioHistory(account));

  useEffect(() => {
    setHistory(readPortfolioHistory(account));
    const id = setInterval(() => setHistory(readPortfolioHistory(account)), 20_000);
    return () => clearInterval(id);
  }, [account, total]);

  const data = useMemo(() => {
    const now = Date.now();
    const real = history.map((s) => ({ t: s.t, value: s.value, real: true }));
    if (real.length >= 2) return [...real, { t: now, value: total, real: true }];
    // seed a flat-ish lead-in anchored to the current value so the card reads
    // as a chart immediately; replaced by real samples as they accumulate
    const seeded = Array.from({ length: 12 }, (_, i) => ({
      t: now - (12 - i) * 20_000,
      value: total * (0.985 + (i / 11) * 0.015),
      real: false,
    }));
    return [...seeded, { t: now, value: total, real: true }];
  }, [history, total]);

  const first = data[0]?.value ?? total;
  const changePct = first > 0 ? ((total - first) / first) * 100 : 0;
  const up = changePct >= 0;
  const color = up ? '#22d3a8' : '#f5484f';
  const hasReal = history.length >= 2;

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-5 h-full flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-foam-500">총 자산 가치</div>
          <div className="text-4xl font-semibold text-foam-100 mt-1 tabular-nums">
            ${total.toLocaleString('en-US', { maximumFractionDigits: 2 })}
          </div>
        </div>
        <div
          className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md ${
            up ? 'text-tide-400 bg-tide-400/10' : 'text-coral-400 bg-coral-500/10'
          }`}
        >
          {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
          {up ? '+' : ''}
          {changePct.toFixed(2)}%
        </div>
      </div>

      <div className="flex-1 min-h-[140px] mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="pfFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="t" hide />
            <YAxis domain={['dataMin', 'dataMax']} hide />
            <Tooltip
              contentStyle={{ background: '#0d1420', border: '1px solid #182437', borderRadius: 8, fontSize: 12 }}
              labelFormatter={(t) => new Date(Number(t)).toLocaleTimeString('ko-KR')}
              formatter={(value) => [`$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}`, '자산']}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill="url(#pfFill)"
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="text-[10px] text-foam-600 mt-2">
        {hasReal
          ? '탭이 열려 있는 동안 20초마다 실시간으로 가치가 기록됩니다.'
          : '실시간 누적 시작 — 탭을 열어두면 시간에 따라 추이가 채워집니다.'}
      </div>
    </div>
  );
}

interface FarmRowData {
  poolId: string;
  pid: number;
  staked: number;
  pending: number;
  value: number;
}

function RewardsCard({
  pendingPlg,
  plgPrice,
  farmRows,
  farmAddress,
}: {
  pendingPlg: number;
  plgPrice: number;
  farmRows: FarmRowData[];
  farmAddress?: Address;
}) {
  const claimable = farmRows.filter((r) => r.pending > 0);
  const usd = pendingPlg * plgPrice;

  return (
    <div className="rounded-xl border border-tide-400/30 bg-tide-400/5 p-5">
      <div className="flex items-center gap-1.5 text-xs text-foam-400">
        <Coins size={13} className="text-tide-400" />
        미수령 파밍 보상
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className="text-3xl font-semibold text-tide-400 tabular-nums transition-all">
          {pendingPlg.toLocaleString('en-US', { maximumFractionDigits: 4 })}
        </span>
        <span className="text-sm text-foam-400">PLG</span>
        {pendingPlg > 0 && <span className="w-1.5 h-1.5 rounded-full bg-tide-400 animate-pulse" />}
      </div>
      <div className="text-xs text-foam-500 mt-0.5 tabular-nums">
        ≈ ${usd.toLocaleString('en-US', { maximumFractionDigits: 2 })} · {claimable.length}개 풀에서 발생 중
      </div>
      <div className="text-[10px] text-foam-600 mt-3">
        {farmAddress
          ? '블록마다 실시간 누적됩니다. 아래 Farm 표에서 풀별로 수확하세요.'
          : '이 네트워크에는 파밍 컨트랙트가 없습니다.'}
      </div>
    </div>
  );
}

function FarmHarvestRow({
  row,
  farmAddress,
  account,
}: {
  row: FarmRowData;
  farmAddress?: Address;
  account: Address;
}) {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const { writeContract, data: hash, isPending, reset } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash });

  useEffect(() => {
    if (isSuccess && hash) {
      logTransaction({
        type: 'harvest',
        hash,
        account,
        chainId: readChainId,
        details: { pool: row.poolId },
      });
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const harvest = () => {
    if (!farmAddress) return;
    writeContract({
      address: farmAddress,
      abi: FARM_ABI,
      functionName: 'deposit',
      args: [BigInt(row.pid), 0n],
    });
  };

  const busy = isPending || isConfirming;

  return (
    <tr className="border-t border-abyss-700">
      <td className="py-2 text-foam-100 font-medium">{row.poolId}</td>
      <td className="py-2 text-right tabular-nums text-foam-300">
        {row.staked.toLocaleString('en-US', { maximumFractionDigits: 6 })}
      </td>
      <td className="py-2 text-right tabular-nums text-tide-400">
        {row.pending.toLocaleString('en-US', { maximumFractionDigits: 4 })}
      </td>
      <td className="py-2 text-right">
        <button
          onClick={harvest}
          disabled={!farmAddress || row.pending <= 0 || busy}
          className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-tide-500 text-abyss-950 hover:bg-tide-400 disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed transition-colors"
        >
          {busy ? '수확 중...' : '수확'}
        </button>
      </td>
    </tr>
  );
}

function ActivityFeed({ items }: { items: ReturnType<typeof useMyActivity> }) {
  return (
    <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
      <h3 className="text-sm font-semibold text-foam-100 mb-3 flex items-center gap-1.5">
        <Clock size={14} className="text-tide-400" />
        내 최근 활동
      </h3>
      {items === null ? (
        <div className="text-xs text-foam-500 py-6 text-center">활동을 불러오는 중...</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-foam-500 py-6 text-center">
          아직 기록된 거래가 없습니다. 스왑·파밍·예치를 실행하면 여기에 표시됩니다.
        </div>
      ) : (
        <div className="space-y-1">
          {items.map((it) => (
            <div key={it.hash} className="flex items-center gap-3 py-1.5 border-b border-abyss-700/50 last:border-0">
              <span className="w-7 h-7 rounded-lg bg-abyss-900 border border-abyss-700 flex items-center justify-center text-tide-400 shrink-0">
                <TrendingUp size={13} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-foam-100">{TX_LABEL[it.type] ?? it.type}</div>
                {it.pool && <div className="text-[10px] text-foam-500 truncate">{it.pool}</div>}
              </div>
              <div className="text-[10px] text-foam-500 shrink-0 tabular-nums">
                {it.at ? new Date(it.at).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
      <h3 className="text-sm font-semibold text-foam-100 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="text-xs text-foam-500 py-4 text-center">{text}</div>;
}
